// AppFolio Direct API client
// Requests go through the Cloud Function proxy at /api/appfolio
// which handles auth and forwards to AppFolio Reports API

const APPFOLIO_API_BASE = '/api/appfolio';

/**
 * Core POST fetch with error handling.
 * @param {string} report - Report type (e.g., 'work_order')
 * @param {Object} filters - Report-specific filters
 * @returns {Promise<Array>} Parsed JSON array of results
 */
export const appfolioFetch = async (report, filters = {}) => {
  const url = `${window.location.origin}${APPFOLIO_API_BASE}`;

  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ report, filters }),
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`AppFolio API ${response.status}: ${text || response.statusText}`);
  }

  return response.json();
};

/**
 * Fetch work orders with optional filters.
 * @param {Object} filters - { status, assigned_user, property_id, etc. }
 * @returns {Promise<Array>} Work orders
 */
export const fetchWorkOrders = (filters = {}) =>
  appfolioFetch('work_order', filters);

/**
 * Fetch labor summary with optional filters.
 * @param {Object} filters - { date_from, date_to, maintenance_tech, etc. }
 * @returns {Promise<Array>} Labor entries
 */
export const fetchLaborSummary = (filters = {}) =>
  appfolioFetch('work_order_labor_summary', filters);

/**
 * Fetch surveys with optional filters.
 * @param {Object} filters - Query parameters
 * @returns {Promise<Array>} Survey records
 */
export const fetchSurveys = (filters = {}) =>
  appfolioFetch('surveys_summary', filters);

/**
 * Fetch inventory usage with optional filters.
 * @param {Object} filters - Query parameters
 * @returns {Promise<Array>} Inventory usage records
 */
export const fetchInventoryUsage = (filters = {}) =>
  appfolioFetch('inventory_usage', filters);

/**
 * Trigger a manual sync via the syncTrigger Cloud Function.
 * Requires Firebase Auth (admin only).
 *
 * @param {string} action - 'frequent' | 'daily' | 'seed'
 * @returns {Promise<Object>} Sync result with counts
 */
export const triggerSync = async (action) => {
  // Import Firebase Auth lazily to avoid circular deps
  const { getAuth } = await import('firebase/auth');
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    throw new Error('Not authenticated');
  }

  const token = await user.getIdToken();
  const url = `${window.location.origin}/api/sync`;

  const fetchOptions = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ action }),
  };

  // Daily/seed syncs take minutes — Firebase Hosting rewrites timeout at 60s.
  // Fire-and-forget: the Cloud Function keeps running independently.
  // Status updates arrive via sync_metadata/state in Firestore.
  if (action === 'daily' || action === 'seed') {
    fetch(url, fetchOptions).catch(() => {});
    return { async: true, action, message: `${action} sync started — status will update above` };
  }

  const response = await fetch(url, fetchOptions);

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || `Sync failed: ${response.status}`);
  }

  return response.json();
};
